import { useCallback, useEffect, useMemo, useState } from "react";
import { DashboardAPI } from "../../api/dashboard";
import { assetUrl } from "../../api/http";
import { PromotionsAdminAPI } from "../../api/promotions";
import type {
  CustomerCartEmailSettings,
  CustomerCartListItem,
  CustomerCartListResponse,
} from "../../types/websiteVisits";

const money = (value: number | undefined, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const resolveCartItemImage = (value?: string) => assetUrl(value) || value || "";
const isObjectId = (value?: string) => /^[a-f\d]{24}$/i.test(String(value || ""));
const generateCartPromoCode = () =>
  `CART-${Math.random().toString(36).slice(2, 7).toUpperCase()}${Date.now()
    .toString(36)
    .slice(-3)
    .toUpperCase()}`;
const formatDiscountLabel = (type: "percentage" | "fixed", value: number) =>
  type === "percentage"
    ? `${Number(value || 0).toFixed(0)}% off`
    : `${money(Number(value || 0))} off`;
const buildCartOfferEmail = (input: {
  code: string;
  discountLabel: string;
  itemNames: string[];
}) => {
  const itemLine = input.itemNames.length
    ? `It is reserved for the item${input.itemNames.length === 1 ? "" : "s"} currently in your cart: ${input.itemNames.join(", ")}.`
    : "It is reserved for the eligible item(s) currently in your cart.";
  return [
    "Hi,",
    "",
    "Your custom cart is still saved, and we created a cart-only offer for you.",
    "",
    `Use promo code ${input.code} at checkout to receive ${input.discountLabel}. ${itemLine}`,
    "",
    "When you are ready, return to checkout securely. If you need help with the photo, chain, size, or any custom detail, reply to this email and we will help before you order.",
    "",
    "Thank you for choosing Beyond Pendants.",
  ].join("\n");
};

const defaultEmailSettings: CustomerCartEmailSettings = {
  enabled: false,
  delayHours: 24,
  cooldownDays: 7,
  defaultCouponCode: "",
  defaultSubject: "Your custom cart is still waiting",
  defaultMessage:
    "Your selected items are saved in your cart. Come back when you are ready and finish your order securely.",
};

const inputStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 0,
  padding: "10px 12px",
  minHeight: 40,
  background: "#fff",
  color: "#0f172a",
};

const buttonStyle: React.CSSProperties = {
  border: "1px solid #0f172a",
  borderRadius: 0,
  background: "#0f172a",
  color: "#fff",
  padding: "10px 14px",
  fontWeight: 900,
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 0,
  background: "#fff",
  color: "#0f172a",
  padding: "10px 14px",
  fontWeight: 900,
  cursor: "pointer",
};

const cartTableColumns = [
  { label: "Customer", width: 160 },
  { label: "Cart Items", width: 420 },
  { label: "Value", width: 80 },
  { label: "Last Updated", width: 112 },
  { label: "Email Status", width: 126 },
  { label: "Last Page", width: 90 },
  { label: "Actions", width: 92 },
] as const;

const cartTableMinWidth = 40 + cartTableColumns.reduce((sum, column) => sum + column.width, 0);
const cartTableCellStyle: React.CSSProperties = {
  padding: "12px 10px",
  borderBottom: "1px solid #eef2f7",
  verticalAlign: "top",
};

export default function ActiveCustomerCartsPage() {
  const [data, setData] = useState<CustomerCartListResponse | null>(null);
  const [settings, setSettings] =
    useState<CustomerCartEmailSettings>(defaultEmailSettings);
  const [loading, setLoading] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [emailStatus, setEmailStatus] = useState<"all" | "sent" | "unsent">(
    "all",
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [removingCartId, setRemovingCartId] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [generatingPromo, setGeneratingPromo] = useState(false);
  const [cartPromo, setCartPromo] = useState<{
    enabled: boolean;
    discountType: "percentage" | "fixed";
    discountValue: number;
    generatedCode: string;
  }>({
    enabled: false,
    discountType: "percentage",
    discountValue: 10,
    generatedCode: "",
  });
  const [composer, setComposer] = useState({
    subject: defaultEmailSettings.defaultSubject,
    message: defaultEmailSettings.defaultMessage,
    couponCode: "",
  });

  const loadCarts = useCallback(() => {
    setLoading(true);
    setError(null);
    DashboardAPI.getCustomerCarts({
      page,
      limit,
      search: search.trim(),
      emailStatus,
    })
      .then((next) => {
        setData(next);
        setSelectedIds((current) =>
          current.filter((id) => next.items.some((row) => row.id === id)),
        );
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Failed to load active carts.");
      })
      .finally(() => setLoading(false));
  }, [emailStatus, limit, page, search]);

  useEffect(() => {
    loadCarts();
  }, [loadCarts]);

  useEffect(() => {
    let cancelled = false;
    setSettingsLoading(true);
    DashboardAPI.getCustomerCartEmailSettings()
      .then((next) => {
        if (cancelled) return;
        setSettings(next);
        setComposer({
          subject: next.defaultSubject,
          message: next.defaultMessage,
          couponCode: next.defaultCouponCode,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message || "Failed to load cart email settings.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setSettingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = data?.items || [];
  const summary = data?.summary;
  const totalValue = useMemo(
    () => money(summary?.subtotal || 0),
    [summary?.subtotal],
  );
  const visibleIds = rows.map((row) => row.id);
  const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
  const activeTargetRows = selectedRows.length ? selectedRows : rows;
  const activeTargetLabel = selectedRows.length
    ? `${selectedRows.length} selected cart${selectedRows.length === 1 ? "" : "s"}`
    : `${rows.length} visible cart${rows.length === 1 ? "" : "s"}`;
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleVisibleSelection = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) => current.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIds((current) => Array.from(new Set([...current, ...visibleIds])));
  };

  const toggleRowSelection = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setNotice(null);
    setError(null);
    try {
      const next = await DashboardAPI.updateCustomerCartEmailSettings(settings);
      setSettings(next);
      setNotice("Cart email settings saved.");
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to save cart email settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const generateCartOnlyPromo = async (
    targetRows: CustomerCartListItem[] = activeTargetRows,
  ) => {
    const productIds = Array.from(
      new Set(
        targetRows
          .flatMap((row) => row.items.map((item) => String(item.productId || "")))
          .filter(isObjectId),
      ),
    );
    if (!targetRows.length) {
      throw new Error("No active carts are available for this promo.");
    }
    if (!productIds.length) {
      throw new Error(
        "Could not find valid product IDs in the selected cart items.",
      );
    }
    const discountValue = Math.max(0, Number(cartPromo.discountValue || 0));
    if (discountValue <= 0) {
      throw new Error("Enter a discount greater than 0 before generating a promo.");
    }
    if (cartPromo.discountType === "percentage" && discountValue > 100) {
      throw new Error("Percentage discount cannot be more than 100%.");
    }

    setGeneratingPromo(true);
    setNotice(null);
    setError(null);
    try {
      const code = generateCartPromoCode();
      const discountLabel = formatDiscountLabel(
        cartPromo.discountType,
        discountValue,
      );
      const itemNames = Array.from(
        new Set(
          targetRows
            .flatMap((row) => row.items.map((item) => String(item.title || "").trim()))
            .filter(Boolean),
        ),
      ).slice(0, 5);
      const coupon = await PromotionsAdminAPI.createCoupon({
        code,
        label: "Cart-only recovery offer",
        description: `Generated from Active Customer Carts for ${targetRows
          .map((row) => row.email)
          .join(", ")}`,
        tokenTitle: "Cart-only offer",
        tokenDescription: `${discountLabel} for the eligible item(s) in this cart.`,
        discountType: cartPromo.discountType,
        discountValue,
        maxUses: Math.max(1, targetRows.length),
        perCustomerLimit: 1,
        isActive: true,
        autoIssue: false,
        requiresEmailCapture: false,
        allowWithReferralReward: false,
        allowWithReferredDiscount: false,
        applicableProductIds: productIds,
      });
      setCartPromo((current) => ({
        ...current,
        enabled: true,
        generatedCode: coupon.code,
      }));
      setComposer({
        subject: "A cart-only offer for your custom order",
        message: buildCartOfferEmail({
          code: coupon.code,
          discountLabel,
          itemNames,
        }),
        couponCode: coupon.code,
      });
      setNotice(
        `Generated promo ${coupon.code} for ${productIds.length} eligible product${productIds.length === 1 ? "" : "s"}.`,
      );
      return coupon.code;
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to generate promo.";
      setCartPromo((current) => ({ ...current, enabled: false }));
      setError(message);
      throw new Error(message);
    } finally {
      setGeneratingPromo(false);
    }
  };

  const sendEmails = async (mode: "selected" | "visible") => {
    setSending(true);
    setNotice(null);
    setError(null);
    try {
      const targetRows =
        mode === "selected"
          ? rows.filter((row) => selectedIds.includes(row.id))
          : rows;
      let couponCode = composer.couponCode;
      if (cartPromo.enabled && !couponCode.trim()) {
        couponCode = await generateCartOnlyPromo(targetRows);
      }
      const result = await DashboardAPI.sendCustomerCartEmails({
        cartIds: mode === "selected" ? selectedIds : visibleIds,
        limit: mode === "selected" ? selectedIds.length : visibleIds.length,
        subject: composer.subject,
        message: composer.message,
        couponCode,
      });
      setNotice(
        `Cart emails processed: ${result.sent} sent, ${result.failed} failed.`,
      );
      loadCarts();
    } catch (err: any) {
      const transport = err?.response?.data?.emailTransport;
      setError(
        [
          err?.response?.data?.message || "Failed to send cart emails.",
          transport?.host ? `SMTP: ${transport.host} as ${transport.user || "no user"}` : "",
        ]
          .filter(Boolean)
          .join(" "),
      );
    } finally {
      setSending(false);
    }
  };

  const removeCart = async (row: CustomerCartListItem) => {
    const ok = window.confirm(
      `Remove ${row.email} from Active Customer Carts? This clears the saved cart from this list.`,
    );
    if (!ok) return;
    setRemovingCartId(row.id);
    setNotice(null);
    setError(null);
    try {
      await DashboardAPI.removeCustomerCart(row.id);
      setNotice(`Removed ${row.email} from active carts.`);
      setSelectedIds((current) => current.filter((id) => id !== row.id));
      loadCarts();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to remove active cart.");
    } finally {
      setRemovingCartId(null);
    }
  };

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div style={{ display: "grid", gap: 5 }}>
        <h1 style={{ margin: 0, fontSize: 26, color: "#0f172a" }}>
          Active Customer Carts
        </h1>
        <p style={{ margin: 0, color: "#475569", fontSize: 14, lineHeight: 1.5 }}>
          Customers with items still in cart, grouped by email. Paid orders are removed
          automatically.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
          border: "1px solid #e2e8f0",
          background: "#fff",
        }}
      >
        {[
          ["Customers", String(summary?.customerCount || 0)],
          ["Items Waiting", String(summary?.itemCount || 0)],
          ["Cart Value", totalValue],
          ["Selected", String(selectedIds.length)],
        ].map(([label, value]) => (
          <div
            key={label}
            style={{
              padding: 16,
              borderRight: "1px solid #e2e8f0",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              {label}
            </div>
            <div style={{ marginTop: 6, fontSize: 26, fontWeight: 900, color: "#0f172a" }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <section style={{ border: "1px solid #e2e8f0", background: "#fff" }}>
        <div style={{ padding: 16, borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ margin: 0, color: "#0f172a", fontSize: 18 }}>
            Cart Email Controls
          </h2>
          <p style={{ margin: "5px 0 0", color: "#475569", fontSize: 13 }}>
            Create a cart-only promo, review the message, then send it to selected
            carts or the visible list.
          </p>
        </div>

        <div style={{ display: "grid", gap: 0 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <div style={{ padding: 16, borderRight: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em" }}>
                1. Automatic reminders
              </div>
              <label style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  title="Send abandoned-cart reminders after the delay below."
                  onChange={(event) =>
                    setSettings((current) => ({
                      ...current,
                      enabled: event.target.checked,
                    }))
                  }
                />
                <span style={{ fontWeight: 900, color: "#0f172a" }}>
                  Send reminders automatically
                </span>
              </label>
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={{ display: "grid", gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
                    After hours
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={720}
                    style={inputStyle}
                    title="Hours to wait after the latest cart update."
                    value={settings.delayHours}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        delayHours: Number(event.target.value) || 24,
                      }))
                    }
                  />
                </label>
                <label style={{ display: "grid", gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
                    Cooldown days
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    style={inputStyle}
                    title="Days to wait before emailing the same cart again."
                    value={settings.cooldownDays}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        cooldownDays: Number(event.target.value) || 7,
                      }))
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                disabled={savingSettings || settingsLoading}
                onClick={saveSettings}
                title="Save automatic reminder timing and default email settings."
                style={{
                  ...secondaryButtonStyle,
                  marginTop: 12,
                  opacity: savingSettings || settingsLoading ? 0.55 : 1,
                }}
              >
                {savingSettings ? "Saving..." : "Save Reminder Settings"}
              </button>
            </div>

            <div style={{ padding: 16, borderRight: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em" }}>
                2. Cart-only promo
              </div>
              <p style={{ margin: "8px 0 0", color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
                Applies only to product IDs in {activeTargetLabel}. Set the discount,
                then check the box to generate and insert the code.
              </p>
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={{ display: "grid", gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
                    Discount type
                  </span>
                  <select
                    style={inputStyle}
                    value={cartPromo.discountType}
                    title="Choose whether the cart promo discounts by percent or dollar amount."
                    onChange={(event) =>
                      setCartPromo((current) => ({
                        ...current,
                        discountType:
                          event.target.value === "fixed" ? "fixed" : "percentage",
                        generatedCode: "",
                      }))
                    }
                  >
                    <option value="percentage">Percent</option>
                    <option value="fixed">Amount</option>
                  </select>
                </label>
                <label style={{ display: "grid", gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
                    Discount
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={cartPromo.discountType === "percentage" ? 100 : undefined}
                    style={inputStyle}
                    title="Discount value for the generated cart-only promo."
                    value={cartPromo.discountValue}
                    onChange={(event) =>
                      setCartPromo((current) => ({
                        ...current,
                        discountValue: Number(event.target.value) || 0,
                        generatedCode: "",
                      }))
                    }
                    placeholder={cartPromo.discountType === "percentage" ? "10" : "15"}
                  />
                </label>
              </div>
              <label style={{ marginTop: 12, display: "flex", alignItems: "flex-start", gap: 10 }}>
                <input
                  type="checkbox"
                  checked={cartPromo.enabled}
                  disabled={generatingPromo || activeTargetRows.length === 0}
                  title="Create a real promo code for the selected or visible cart items."
                  onChange={(event) => {
                    if (!event.target.checked) {
                      setCartPromo((current) => ({
                        ...current,
                        enabled: false,
                        generatedCode: "",
                      }));
                      return;
                    }
                    void generateCartOnlyPromo();
                  }}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ display: "block", fontWeight: 900, color: "#0f172a" }}>
                    Generate and insert promo code
                  </span>
                  <span style={{ display: "block", marginTop: 3, color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
                    {generatingPromo
                      ? "Generating coupon..."
                      : cartPromo.generatedCode
                        ? `Ready: ${cartPromo.generatedCode}`
                        : "Creates a real coupon and fills the email below."}
                  </span>
                </span>
              </label>
            </div>

            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em" }}>
                3. Send email
              </div>
              <p style={{ margin: "8px 0 0", color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
                The message is ready to send, but you can edit any line first.
              </p>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  disabled={sending || selectedIds.length === 0}
                  onClick={() => sendEmails("selected")}
                  title="Email only the carts you checked in the table."
                  style={{
                    ...buttonStyle,
                    opacity: sending || selectedIds.length === 0 ? 0.55 : 1,
                  }}
                >
                  Send Selected
                </button>
                <button
                  type="button"
                  disabled={sending || visibleIds.length === 0}
                  onClick={() => sendEmails("visible")}
                  title="Email every cart currently visible on this page after filters."
                  style={{
                    ...secondaryButtonStyle,
                    opacity: sending || visibleIds.length === 0 ? 0.55 : 1,
                  }}
                >
                  Send Visible
                </button>
              </div>
            </div>
          </div>

          <div style={{ padding: 16, display: "grid", gap: 10 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                gap: 10,
              }}
            >
              <input
                style={inputStyle}
                value={composer.subject}
                title="Subject line customers will see in their inbox."
                onChange={(event) =>
                  setComposer((current) => ({ ...current, subject: event.target.value }))
                }
                placeholder="Subject"
              />
              <input
                style={inputStyle}
                value={composer.couponCode}
                title="Optional promo code included in the cart email."
                onChange={(event) =>
                  setComposer((current) => ({
                    ...current,
                    couponCode: event.target.value.toUpperCase(),
                  }))
                }
                placeholder="Promo code"
              />
            </div>
            <textarea
              style={{ ...inputStyle, minHeight: 150, resize: "vertical", lineHeight: 1.55 }}
              value={composer.message}
              title="Editable email body. The styled email adds cart details and checkout link."
              onChange={(event) =>
                setComposer((current) => ({ ...current, message: event.target.value }))
              }
              placeholder="Email message"
            />
          </div>
        </div>
      </section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
          gap: 10,
          alignItems: "end",
        }}
      >
        <label style={{ display: "grid", gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
            Search email or item
          </span>
          <input
            style={inputStyle}
            value={search}
            title="Filter carts by customer email or item name."
            onChange={(event) => {
              setPage(1);
              setSearch(event.target.value);
            }}
            placeholder="customer@email.com or pendant"
          />
        </label>
        <label style={{ display: "grid", gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
            Rows
          </span>
          <select
            style={inputStyle}
            value={limit}
            title="How many active carts to show per page."
            onChange={(event) => {
              setPage(1);
              setLimit(Number(event.target.value));
            }}
          >
            {[10, 25, 50, 100].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "grid", gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
            Email status
          </span>
          <select
            style={inputStyle}
            value={emailStatus}
            title="Filter active carts by whether a cart email was sent."
            onChange={(event) => {
              setPage(1);
              setEmailStatus(
                event.target.value === "sent" || event.target.value === "unsent"
                  ? event.target.value
                  : "all",
              );
            }}
          >
            <option value="all">All carts</option>
            <option value="sent">Email sent</option>
            <option value="unsent">Email not sent</option>
          </select>
        </label>
      </div>

      {notice ? (
        <div
          style={{
            border: "1px solid #bbf7d0",
            background: "#f0fdf4",
            color: "#166534",
            padding: "12px 14px",
          }}
        >
          {notice}
        </div>
      ) : null}

      {error ? (
        <div
          style={{
            border: "1px solid #fecdd3",
            background: "#fff1f2",
            color: "#9f1239",
            padding: "12px 14px",
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ border: "1px solid #e2e8f0", background: "#fff", overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            minWidth: cartTableMinWidth,
            tableLayout: "fixed",
            borderCollapse: "collapse",
          }}
        >
          <colgroup>
            <col style={{ width: 40 }} />
            {cartTableColumns.map((column) => (
              <col key={column.label} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead style={{ background: "#f8fafc" }}>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "12px 10px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleVisibleSelection}
                  aria-label="Select visible carts"
                  title="Select or clear all visible carts."
                />
              </th>
              {cartTableColumns.map((column) => (
                <th
                  key={column.label}
                  style={{
                    textAlign: "left",
                    padding: "12px 10px",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#475569",
                    fontSize: 12,
                    width: column.width,
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td style={cartTableCellStyle}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleRowSelection(row.id)}
                    aria-label={`Select ${row.email}`}
                    title="Select this cart for manual email actions."
                  />
                </td>
                <td style={cartTableCellStyle}>
                  <div style={{ fontWeight: 900, color: "#0f172a", overflowWrap: "anywhere" }}>
                    {row.email}
                  </div>
                  <div style={{ marginTop: 5, fontSize: 12, color: "#64748b" }}>
                    {row.guestId ? `Guest ${row.guestId}` : row.authUid ? `UID ${row.authUid}` : "Email identified"}
                  </div>
                </td>
                <td style={cartTableCellStyle}>
                  <div style={{ display: "grid", gap: 8 }}>
                    {row.items.map((item, index) => {
                      const imageSrc = resolveCartItemImage(item.image);
                      return (
                        <div
                          key={`${row.id}-${item.productId || item.title}-${index}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: imageSrc ? "44px minmax(0,1fr)" : "minmax(0,1fr)",
                            gap: 10,
                            alignItems: "center",
                          }}
                        >
                          {imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={item.title}
                              onError={(event) => {
                                event.currentTarget.style.visibility = "hidden";
                              }}
                              style={{
                                width: 44,
                                height: 44,
                                objectFit: "cover",
                                border: "1px solid #e2e8f0",
                                background: "#f8fafc",
                              }}
                            />
                          ) : null}
                          <div>
                            <div
                              style={{
                                fontWeight: 800,
                                color: "#0f172a",
                                lineHeight: 1.35,
                                overflowWrap: "break-word",
                              }}
                            >
                              {item.title} x{item.quantity}
                            </div>
                            <div style={{ marginTop: 3, fontSize: 12, color: "#64748b" }}>
                              {[item.categoryLabel, item.selectedColor, item.selectedChain, item.selectedSize]
                                .filter(Boolean)
                                .join(" / ") || "No options"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </td>
                <td style={{ ...cartTableCellStyle, fontWeight: 900, color: "#0f172a" }}>
                  {money(row.subtotal, row.currency)}
                </td>
                <td style={{ ...cartTableCellStyle, color: "#0f172a" }}>
                  {formatDateTime(row.updatedAt)}
                </td>
                <td style={cartTableCellStyle}>
                  <div style={{ color: row.lastReminderEmailError ? "#9f1239" : "#0f172a", fontWeight: 800 }}>
                    {row.lastReminderEmailError
                      ? "Failed"
                      : row.lastReminderEmailSentAt
                        ? "Sent"
                        : "Not sent"}
                  </div>
                  <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>
                    {row.lastReminderEmailSentAt
                      ? formatDateTime(row.lastReminderEmailSentAt)
                      : `${row.reminderEmailCount || 0} emails`}
                  </div>
                  {row.lastReminderCouponCode ? (
                    <div style={{ marginTop: 4, color: "#166534", fontSize: 12, fontWeight: 800 }}>
                      {row.lastReminderCouponCode}
                    </div>
                  ) : null}
                  {row.lastReminderEmailError ? (
                    <div style={{ marginTop: 4, color: "#9f1239", fontSize: 12, overflowWrap: "anywhere" }}>
                      {row.lastReminderEmailError}
                    </div>
                  ) : null}
                </td>
                <td style={{ ...cartTableCellStyle, color: "#64748b", overflowWrap: "anywhere" }}>
                  {row.lastPath || "-"}
                </td>
                <td style={cartTableCellStyle}>
                  <button
                    type="button"
                    disabled={removingCartId === row.id}
                    onClick={() => removeCart(row)}
                    title="Remove this cart from Active Customer Carts."
                    style={{
                      ...secondaryButtonStyle,
                      borderColor: "#fecaca",
                      color: "#b91c1c",
                      opacity: removingCartId === row.id ? 0.55 : 1,
                      padding: "8px 10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {removingCartId === row.id ? "Removing..." : "Remove"}
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={8} style={{ padding: 16, color: "#64748b" }}>
                  {loading ? "Loading active carts..." : "No active carts with emails yet."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ color: "#64748b", fontSize: 13 }}>
          {data?.total || 0} active carts, page {data?.page || page} of {data?.pages || 1}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            title="Go to the previous page of active carts."
            style={{
              ...secondaryButtonStyle,
              opacity: page <= 1 ? 0.55 : 1,
              cursor: page <= 1 ? "default" : "pointer",
            }}
          >
            Prev
          </button>
          <button
            type="button"
            disabled={page >= (data?.pages || 1)}
            onClick={() => setPage((current) => Math.min(data?.pages || 1, current + 1))}
            title="Go to the next page of active carts."
            style={{
              ...secondaryButtonStyle,
              opacity: page >= (data?.pages || 1) ? 0.55 : 1,
              cursor: page >= (data?.pages || 1) ? "default" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
